/**
 * Service-first commercial pages (buyer intent, not location-first).
 * Written for helpful-content / E-E-A-T: clear buyer, process, proof, FAQs.
 */
export const commercialServices = [
  {
    slug: 'phlebotomy-services-kerala',
    title: 'Phlebotomy Services in Kerala | Homvisit B2B Teams',
    h1: 'Phlebotomy Services in Kerala',
    keyword: 'phlebotomy services Kerala',
    description: 'Homvisit provides statewide phlebotomy services in Kerala for corporates, diagnostic labs, and healthcare partners — camps, onsite teams, and collection…',
    audience: 'Corporates, diagnostic labs, hospitals, and healthcare partners',
    intro: [
      'Homvisit delivers professional phlebotomy services across Kerala for organisations that need reliable sample collection — not a consumer booking app.',
      'Whether you run corporate health camps, need a lab collection partner, or want to outsource mobile phlebotomy, we deploy certified phlebotomists with clear SOPs, labelling discipline, and coordinated sample handoff.',
    ],
    benefits: [
      'Statewide coverage across all 14 districts',
      'Corporate camps and onsite workplace collection',
      'Diagnostic lab and white-label partnership options',
      'Trained phlebotomists with hygiene and ID protocols',
      'Single point of contact for planning and day-of execution',
    ],
    process: [
      { title: 'Scope the need', text: 'Camp size, locations, test panels, and timing windows.' },
      { title: 'Deploy the team', text: 'Certified phlebotomists assigned by district and headcount.' },
      { title: 'Collect & document', text: 'ID verification, sterile draw, labelling, and chain notes.' },
      { title: 'Handoff', text: 'Coordinated transfer to your lab or designated logistics partner.' },
    ],
    faqs: [
      {
        q: 'Do you serve only Kochi or all of Kerala?',
        a: 'We cover Kerala statewide, with denser corporate activity in Ernakulam/Kochi, Trivandrum, Kozhikode, and Thrissur — plus on-demand teams in other districts.',
      },
      {
        q: 'Is this home blood collection for individuals?',
        a: 'Individual home collection is available as a secondary service. Our primary model is B2B: camps, labs, and organisational partners.',
      },
      {
        q: 'Can you work under our brand?',
        a: 'Yes — white-label / partner collection models are available for diagnostic labs. See our white-label phlebotomy page.',
      },
    ],
    related: [
      '/services/corporate-health-camps',
      '/services/mobile-phlebotomy',
      '/services/diagnostic-lab-partner',
      '/insights/how-to-outsource-home-blood-collection-kerala',
    ],
  },
  {
    slug: 'home-blood-collection',
    title: 'Home Blood Collection Partner Guide | Homvisit Kerala',
    h1: 'Home Blood Collection Services in Kerala',
    keyword: 'home collection partner Kerala',
    description: 'Partner with Homvisit for home blood collection in Kerala — trained phlebotomists, scheduled visits, and B2B collection support for labs and healthcare brands.',
    audience: 'Diagnostic labs, healthcare brands, and organisations needing home collection capacity',
    intro: [
      'Homvisit operates as a home blood collection partner in Kerala for labs and healthcare organisations that need dependable doorstep sample collection without building a full field team.',
      'We focus on trained phlebotomists, clear patient ID checks, and disciplined sample handling so your brand’s collection experience stays consistent.',
    ],
    benefits: [
      'Home collection capacity without hiring full-time field staff',
      'Phlebotomists familiar with Kerala geography and languages',
      'Partner-friendly workflows for labs and healthcare brands',
      'Secondary consumer requests handled with the same quality bar',
    ],
    process: [
      { title: 'Partner onboarding', text: 'Align SOPs, branding rules, and reporting expectations.' },
      { title: 'Slot assignment', text: 'Match a phlebotomist to location and preferred time window.' },
      { title: 'Home visit', text: 'Identity check, consent, sterile collection, labelling.' },
      { title: 'Sample movement', text: 'Handoff into your logistics or designated drop point.' },
    ],
    faqs: [
      {
        q: 'Can Homvisit be our home collection partner?',
        a: 'Yes. Many diagnostic and healthcare partners use Homvisit for Kerala home collection capacity while retaining lab processing and reporting.',
      },
      {
        q: 'Do you cover rural districts?',
        a: 'Coverage is strongest in major corridors; rural and hill districts are planned case-by-case based on volume and logistics.',
      },
    ],
    related: [
      '/services/home-collection-network',
      '/services/sample-collection-services',
      '/how-it-works/home-blood-collection',
      '/insights/how-hospitals-scale-home-collection',
    ],
  },
  {
    slug: 'corporate-health-camps',
    title: 'Corporate Health Camps Kerala | Homvisit B2B Teams',
    h1: 'Corporate Health Camps in Kerala',
    keyword: 'corporate health camps Kerala',
    description: 'Plan and run corporate health camps in Kerala with Homvisit — onsite phlebotomy teams, employee screening support, and camp execution across districts.',
    audience: 'HR, admin, and corporate wellness buyers',
    intro: [
      'Corporate health camps help employers screen employees on-site with minimal downtime. Homvisit supplies the phlebotomy and collection backbone so your HR team can focus on communication and participation.',
      'We support offices, factories, campuses, and multi-location rollouts across Kerala.',
    ],
    benefits: [
      'Onsite camps that reduce employee absenteeism for checkups',
      'Scalable teams for 20 to several hundred participants',
      'Clear flow: registration → collection → labelling → dispatch',
      'Experience coordinating with corporate schedules and shift patterns',
    ],
    process: [
      { title: 'Camp brief', text: 'Headcount, shifts, venue constraints, and test list.' },
      { title: 'Staffing plan', text: 'Phlebotomist count, consumables, and queue design.' },
      { title: 'Camp day', text: 'ID verification, draws, labelling, and real-time coordination.' },
      { title: 'Close-out', text: 'Sample counts, exceptions log, and handoff confirmation.' },
    ],
    faqs: [
      {
        q: 'How early should we book a corporate health camp?',
        a: 'Ideally 1–2 weeks for single-site camps; multi-district rollouts need longer planning for staffing.',
      },
      {
        q: 'Can you run camps outside Kochi?',
        a: 'Yes — Trivandrum, Kozhikode, Thrissur, Palakkad industrial sites, and other districts on request.',
      },
    ],
    related: [
      '/services/corporate-health-checkups',
      '/how-it-works/corporate-medical-camps',
      '/resources/health-camp-checklist',
      '/insights/corporate-health-camps-planning-checklist',
    ],
  },
  {
    slug: 'corporate-health-checkups',
    title: 'Corporate Health Checkups Kerala Guide | Homvisit Teams',
    h1: 'Corporate Health Checkups in Kerala',
    keyword: 'corporate health checkups Kerala',
    description: 'Employee health checkup camps and workplace screening support across Kerala with Homvisit phlebotomy teams. Homvisit serves labs, hospitals, and HR teams…',
    audience: 'HR and people-ops teams running annual or pre-employment checkups',
    intro: [
      'Annual and periodic corporate health checkups work best when collection is frictionless. Homvisit provides the onsite phlebotomy layer for employee health screening programs in Kerala.',
      'We align with your checkup package partner or diagnostic lab so employees complete blood draws at the workplace.',
    ],
    benefits: [
      'Workplace checkups with less disruption',
      'Support for annual, pre-employment, and campaign screenings',
      'Coordination with your chosen diagnostic partner',
      'Statewide staffing for multi-office companies',
    ],
    process: [
      { title: 'Package alignment', text: 'Confirm which samples/tests your lab requires onsite.' },
      { title: 'Employee scheduling', text: 'Batch slots by department or shift.' },
      { title: 'Onsite collection', text: 'Phlebotomy stations with privacy and hygiene controls.' },
      { title: 'Reporting path', text: 'Samples move to your lab; reports follow your partner process.' },
    ],
    faqs: [
      {
        q: 'Do you provide doctors and full checkup packages?',
        a: 'Our core strength is phlebotomy and sample collection. Clinical packages and reporting typically sit with your diagnostic/hospital partner; we integrate with them.',
      },
    ],
    related: [
      '/services/corporate-health-camps',
      '/resources/corporate-wellness-guide',
      '/resources/corporate-health-screening-guide',
    ],
  },
  {
    slug: 'mobile-phlebotomy',
    title: 'Mobile Phlebotomy Services Kerala | Homvisit Teams',
    h1: 'Mobile Phlebotomy Services in Kerala',
    keyword: 'mobile phlebotomy Kerala',
    description: 'Mobile phlebotomy in Kerala for camps, workplaces, and partner home visits — trained collectors who travel to the sample, not the other way around.',
    audience: 'Labs and organisations needing field collection capacity',
    intro: [
      'Mobile phlebotomy means the collector comes to the workplace, camp venue, or home — critical across Kerala’s spread-out districts.',
      'Homvisit fields mobile phlebotomists with two-wheeler mobility, sterile kits, and district familiarity.',
    ],
    benefits: [
      'Field teams for camps and doorstep partner visits',
      'Faster coverage across city + peri-urban clusters',
      'Useful for multi-site corporates in one day',
    ],
    process: [
      { title: 'Route plan', text: 'Cluster visits by geography to protect sample timelines.' },
      { title: 'Mobile kit check', text: 'Consumables, labels, cold-pack readiness where required.' },
      { title: 'On-site draw', text: 'Standard phlebotomy + documentation.' },
      { title: 'Return / handoff', text: 'Move samples into agreed logistics path.' },
    ],
    faqs: [
      {
        q: 'Is mobile phlebotomy the same as home collection?',
        a: 'Home collection is one use case. Mobile phlebotomy also covers camps, factories, and multi-location corporate sites.',
      },
    ],
    related: [
      '/services/home-blood-collection',
      '/services/sample-collection-services',
      '/how-it-works/sample-transport',
    ],
  },
  {
    slug: 'sample-collection-services',
    title: 'Blood Sample Collection Company Kerala Guide | Homvisit',
    h1: 'Sample Collection Services in Kerala',
    keyword: 'blood sample collection company Kerala',
    description: 'Homvisit is a sample collection company in Kerala for corporates and diagnostic partners — organised phlebotomy, labelling, and handoff. Homvisit serves labs…',
    audience: 'Labs, hospitals, and B2B healthcare buyers',
    intro: [
      'Looking for a blood sample collection company in Kerala? Homvisit specialises in the collection layer: people, process, and field execution.',
      'We help partners scale collection without compromising identity checks, labelling accuracy, or sample integrity.',
    ],
    benefits: [
      'Dedicated collection operations mindset',
      'Camp + home + workplace formats',
      'SOP-driven labelling and exception handling',
      'Partner reporting on volumes and issues',
    ],
    process: [
      { title: 'Define collection model', text: 'Camp, home network, or hybrid.' },
      { title: 'Train to your SOP', text: 'Align tube types, order of draw, and forms.' },
      { title: 'Execute', text: 'Field collection with supervision for larger camps.' },
      { title: 'Audit loop', text: 'Review rejects, redraws, and process gaps.' },
    ],
    faqs: [
      {
        q: 'Are you a diagnostic lab?',
        a: 'No — we are a collection and phlebotomy services partner. Lab testing and reporting remain with NABL/accredited labs you choose.',
      },
    ],
    related: [
      '/services/diagnostic-lab-partner',
      '/resources/sample-collection-sop',
      '/insights/choosing-phlebotomy-partner-diagnostic-lab',
    ],
  },
  {
    slug: 'diagnostic-lab-partner',
    title: 'Diagnostic Lab Collection Partner Kerala | Homvisit',
    h1: 'Diagnostic Lab Collection Partner',
    keyword: 'diagnostic collection partner',
    description: 'Become a stronger diagnostic brand in Kerala with Homvisit as your sample collection and phlebotomy partner — camps, home network, and white-label options.',
    audience: 'Diagnostic labs and pathology networks',
    intro: [
      'Diagnostic labs win on turnaround and trust. Homvisit acts as your diagnostic collection partner in Kerala so you can expand geography without over-hiring.',
      'We support NABL-minded workflows: identity, labelling, and careful sample handling before your lab’s quality system takes over.',
    ],
    benefits: [
      'Expand collection footprint faster',
      'Overflow staffing for camp seasons',
      'White-label friendly field presence',
      'Aligned to partner SOPs and rejection metrics',
    ],
    process: [
      { title: 'Commercial + SOP alignment', text: 'Branding, SLAs, reject definitions.' },
      { title: 'Pilot district', text: 'Start with one city cluster, measure quality.' },
      { title: 'Scale', text: 'Add districts as volumes justify staffing.' },
      { title: 'Review cadence', text: 'Weekly or monthly quality and volume reviews.' },
    ],
    faqs: [
      {
        q: 'Do you claim NABL accreditation yourselves?',
        a: 'NABL applies to laboratories. Homvisit supports collection practices that help NABL labs protect sample integrity upstream.',
      },
    ],
    related: [
      '/services/white-label-phlebotomy',
      '/resources/nabl-guidelines',
      '/insights/choosing-phlebotomy-partner-diagnostic-lab',
    ],
  },
  {
    slug: 'healthcare-staffing',
    title: 'Phlebotomy Healthcare Staffing Kerala Guide | Homvisit',
    h1: 'Healthcare Staffing for Phlebotomy in Kerala',
    keyword: 'healthcare staffing Kerala',
    description: 'Healthcare staffing in Kerala focused on phlebotomists for camps, labs, and collection projects — contract teams via Homvisit network. Homvisit serves labs…',
    audience: 'Labs, hospitals, and camp organisers needing temporary phlebotomy staff',
    intro: [
      'When you need phlebotomy capacity for a camp wave or seasonal surge, hiring full-time is slow. Homvisit’s healthcare staffing model taps an always-open phlebotomist network across Kerala.',
      'We match certified collectors to short-term and project deployments.',
    ],
    benefits: [
      'Faster staffing than permanent hiring cycles',
      'District-wise talent pool',
      'Useful for camps, pop-ups, and overflow routes',
    ],
    process: [
      { title: 'Role brief', text: 'Dates, locations, skill level, language needs.' },
      { title: 'Shortlist', text: 'Pull from Homvisit network by district.' },
      { title: 'Deploy', text: 'Briefing on your SOP before day one.' },
      { title: 'Feedback', text: 'Performance notes for future matching.' },
    ],
    faqs: [
      {
        q: 'Is this a general nursing agency?',
        a: 'We specialise in phlebotomy / sample collection staffing rather than broad nursing placements.',
      },
    ],
    related: [
      '/jobs',
      '/services/phlebotomy-services-kerala',
      '/how-it-works/phlebotomist-training-standards',
    ],
  },
  {
    slug: 'white-label-phlebotomy',
    title: 'White-Label Phlebotomy Services Kerala Guide | Homvisit',
    h1: 'White-Label Phlebotomy Services',
    keyword: 'NABL sample collection partner',
    description: 'White-label phlebotomy and sample collection partnership in Kerala — Homvisit field teams operating to your brand and SOP. Homvisit serves labs, hospitals…',
    audience: 'Labs and healthcare brands that want collection under their name',
    intro: [
      'White-label phlebotomy lets your brand own the customer experience while Homvisit runs field collection.',
      'Ideal for diagnostic networks expanding in Kerala without building every district team in-house.',
    ],
    benefits: [
      'Your brand, our field execution',
      'SOP and uniform/ID rules as agreed',
      'Scalable across districts',
    ],
    process: [
      { title: 'Brand kit', text: 'Scripts, ID badges, and communication rules.' },
      { title: 'SOP lock', text: 'Tubes, forms, rejection criteria.' },
      { title: 'Silent launch', text: 'Pilot routes under your brand.' },
      { title: 'Scale', text: 'Expand geography with shared KPIs.' },
    ],
    faqs: [
      {
        q: 'Will patients know Homvisit?',
        a: 'In white-label mode, field presentation follows your brand guidelines.',
      },
    ],
    related: [
      '/services/diagnostic-lab-partner',
      '/services/home-collection-network',
      '/resources/sample-collection-sop',
    ],
  },
  {
    slug: 'home-collection-network',
    title: 'Home Collection Network Kerala | Homvisit B2B Teams',
    h1: 'Home Collection Network in Kerala',
    keyword: 'sample collection agency Kerala',
    description: 'Build or extend a home collection network in Kerala with Homvisit — phlebotomist coverage, partner SOPs, and district reach. Homvisit serves labs, hospitals…',
    audience: 'Labs and platforms needing Kerala home-collection reach',
    intro: [
      'A home collection network is more than a few freelancers — it needs coverage maps, backup collectors, and quality loops.',
      'Homvisit’s Kerala network helps partners offer dependable home and mobile collection with an agency-style operating model.',
    ],
    benefits: [
      'District coverage planning',
      'Backup staffing for no-shows',
      'Quality feedback into the network',
      'Bridge between your app/ops and field reality',
    ],
    process: [
      { title: 'Coverage map', text: 'Prioritise cities and response expectations.' },
      { title: 'Network briefing', text: 'Onboard collectors to partner rules.' },
      { title: 'Live operations', text: 'Assign, collect, escalate exceptions.' },
      { title: 'Network health', text: 'Track punctuality, redraws, and complaints.' },
    ],
    faqs: [
      {
        q: 'Can startups use this network?',
        a: 'Yes — platforms and new diagnostic brands often start with a partner network before hiring in-house.',
      },
    ],
    related: [
      '/services/home-blood-collection',
      '/services/mobile-phlebotomy',
      '/insights/how-to-outsource-home-blood-collection-kerala',
    ],
  },
]

export function getCommercialService(slug) {
  return commercialServices.find((p) => p.slug === slug) || null
}

export const commercialServiceSlugs = commercialServices.map((p) => p.slug)
